<template>
  <transition name="profile-fade" appear>
    <div v-if="visible" class="profile-overlay">
      <div class="profile-container">
        <div class="profile-left">
          <div class="profile-left-inner">
            <div class="profile-username" :class="{ 'editing': editingUsername }">
              <template v-if="!editingUsername">{{ userDisplayName }}</template>
              <template v-else>
                <input v-model="newUsername" class="username-input" />
              </template>
            </div>
            <div class="profile-actions">
              <a class="profile-link" v-if="!editingUsername" @click="editingUsername = true">change your username</a>
              <a class="profile-link" v-else @click="saveUsername">validate the username</a>
            </div>
            <div class="profile-bottom-actions">
              <a class="profile-delete-link" @click="confirmDeleteAccount">delete your account</a>
              <span class="profile-delete-link no-underline">—</span>
              <a class="profile-delete-link" @click="logout">log out</a>
            </div>
            <div class="profile-support-buttons">
              <a href="https://ko-fi.com/nuanceradio" target="_blank" rel="noopener" class="profile-support-btn tip">
                <img src="/playground_assets/tip.png" alt="Tip" class="support-icon" />
                Tip me
              </a>
              <a href="https://www.paypal.com/donate/?hosted_button_id=SXGA8V28PV4LL" target="_blank" rel="noopener" class="profile-support-btn donate">
                <img src="/playground_assets/donate.png" alt="Donate" class="support-icon" />
                Donate
              </a>
            </div>
          </div>
        </div>
        <div class="profile-right" :class="{ 'scrollable': favorites.length > 12 }" v-show="favoritesLoaded">
          <div class="profile-title">your favorites tracks</div>
          <div v-if="!favorites.length" class="profile-empty">No favorites yet.</div>
          <div v-else class="profile-grid grid-pasttrack" :style="gridColumnsStyle">
            <last-track
              v-for="(track, idx) in favorites"
              :key="track.id || (track.title + '-' + idx)"
              :artist="track.artist"
              :title="track.title"
              :album="track.album"
              :year="track.year"
              :discogsUrl="track.discogs_url"
              :imageUrl="track.image_url"
              :favorited="true"
              @toggle-favorite="toggleFavorite(idx, track)"
              @open-signup="$emit('open-signup')"
            />
          </div>
        </div>
        <button class="profile-close" @click="$emit('close')">×</button>
      </div>
    </div>
  </transition>
</template>

<script>
import { auth } from '../firebase';
import { onAuthStateChanged, updateProfile, signOut, deleteUser } from 'firebase/auth';
import LastTrack from './last-track.vue';
import api from '../api';
import Vue from 'vue';
export default {
  name: 'ProfileOverlay',
  components: { LastTrack },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      user: null,
      editingUsername: false,
      newUsername: '',
      favorites: [],
      loading: false,
      favoritesLoaded: false
    }
  },
  computed: {
    userDisplayName() {
      if (!this.user) return '';
      return this.user.displayName || this.user.email || 'username';
    },
    gridColumnsStyle() {
      // Only apply on small screens
      if (window.innerWidth > 600) return {};
      const n = this.favorites.length;
      if (n === 1) {
        return { gridTemplateColumns: '1fr' };
      } else if (n === 2 || n === 3) {
        return { gridTemplateColumns: 'repeat(2, 1fr)' };
      }
      return {};
    }
  },
  created() {
    // Try to get favorites from global state immediately
    this.checkGlobalFavorites();
    
    onAuthStateChanged(auth, user => {
      this.user = user;
      this.newUsername = user && user.displayName ? user.displayName : '';
      // Preload favorites immediately when user is authenticated
      if (user) {
        this.fetchFavorites();
      }
    });
    // Listen for global favorites-updated event
    Vue.prototype.$eventBus = Vue.prototype.$eventBus || new Vue();
    this.$eventBus = Vue.prototype.$eventBus;
    this.$eventBus.$on('favorites-updated', this.fetchFavorites);
    
    // Add ESC key listener for desktop
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    if (this.$eventBus) {
      this.$eventBus.$off('favorites-updated', this.fetchFavorites);
    }
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleKeydown(event) {
      // Close modal on ESC key (desktop only)
      if (event.key === 'Escape' && this.visible && window.innerWidth > 768) {
        this.$emit('close');
      }
    },
    checkGlobalFavorites() {
      // Check if favorites are already loaded in the main components
      let foundFavorites = false;
      
      // Check immediate parent
      if (this.$parent && this.$parent.favorites && this.$parent.favoritesLoaded) {
        this.favorites = this.$parent.favorites;
        this.favoritesLoaded = true;
        this.loading = false;
        foundFavorites = true;
        console.log('🎵 Using global favorites from parent:', this.favorites.length, 'tracks');
      }
      
      // Check grandparent (App.vue)
      if (!foundFavorites && this.$parent && this.$parent.$parent && 
          this.$parent.$parent.favorites && this.$parent.$parent.favoritesLoaded) {
        this.favorites = this.$parent.$parent.favorites;
        this.favoritesLoaded = true;
        this.loading = false;
        foundFavorites = true;
        console.log('🎵 Using global favorites from grandparent:', this.favorites.length, 'tracks');
      }
      
      // Check root component
      if (!foundFavorites && this.$root && this.$root.favorites && this.$root.favoritesLoaded) {
        this.favorites = this.$root.favorites;
        this.favoritesLoaded = true;
        this.loading = false;
        foundFavorites = true;
        console.log('🎵 Using global favorites from root:', this.favorites.length, 'tracks');
      }
    },
    
    async fetchFavorites() {
      if (!this.user) return;
      
      // Check global favorites first
      this.checkGlobalFavorites();
      if (this.favoritesLoaded) return;
      
      this.loading = true;
      try {
        const res = await api.get(`/favorites/${this.user.uid}`);
        this.favorites = res.data || [];
        this.favoritesLoaded = true;
      } catch (e) {
        this.favorites = [];
        this.favoritesLoaded = false;
      }
      this.loading = false;
    },
    async saveUsername() {
      if (!this.newUsername || !this.user) return;
      await updateProfile(this.user, { displayName: this.newUsername });
      this.editingUsername = false;
      this.user.displayName = this.newUsername;
    },
    async logout() {
      await signOut(auth);
      this.$emit('close');
    },
    async toggleFavorite(idx, track) {
      // Un-favoriting removes the track from the grid and backend
      if (!this.user) return;
      try {
        await api.delete(`/favorites/${this.user.uid}`, { data: { id: track.id, artist: track.artist, title: track.title } });
        this.$delete(this.favorites, idx);
      } catch (e) {}
    },
    async confirmDeleteAccount() {
      if (!window.confirm('Are you sure you want to delete your account and all associated data? This action cannot be undone.')) return;
      await this.deleteAccount();
    },
    async deleteAccount() {
      if (!this.user) return;
      try {
        // Delete all favorites from backend
        await api.delete(`/favorites/${this.user.uid}`, { data: { deleteAll: true } });
      } catch (e) {}
      try {
        // Delete user from Firebase
        await deleteUser(this.user);
      } catch (e) {
        alert('Error deleting account. You may need to re-authenticate.');
        return;
      }
      this.$emit('close');
    }
  },
  watch: {
    user(newUser) {
      if (newUser) this.fetchFavorites();
    },
    visible(newVisible) {
      // Check for global favorites when modal becomes visible
      if (newVisible && !this.favoritesLoaded) {
        this.checkGlobalFavorites();
        if (!this.favoritesLoaded) {
          this.fetchFavorites();
        }
      }
    }
  }
};
</script>

<style scoped>
.profile-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 20000;
  background: rgba(255,255,255,0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
}
.profile-container {
  display: flex;
  flex-direction: row;
  background: #fff;
  border-radius: 0;
  box-shadow: 0 2px 24px rgba(0,0,0,0.12);
  padding: 0;
  width: 80vw;
  max-width: 1100px;
  min-width: 320px;
  height: auto;
  min-height: 400px;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}
.profile-left {
  background: #f5f5f5;
  width: 28%;
  min-width: 180px;
  max-width: 340px;
  flex-basis: 28%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-top-left-radius: 0.5rem;
  border-bottom-left-radius: 0.5rem;
}
.profile-left-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  min-height: 500px;
}
.profile-username {
  font-size: clamp(1.1rem, 2vw, 1.7rem);
  font-family: Syne, sans-serif;
  font-weight: 700;
  color: #222;
  margin-bottom: 1.2rem;
  text-align: center;
}
.profile-username.editing {
  color: #888;
}
.username-input {
  font-size: clamp(1.1rem, 2vw, 1.7rem);
  font-family: Syne, sans-serif;
  font-weight: 700;
  color: #222;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 0.2rem;
  text-align: center;
  width: 90%;
  margin-bottom: 1.2rem;
}
.profile-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}
.profile-link {
  color: #222;
  font-size: clamp(0.95rem, 1.2vw, 1.05rem);
  text-decoration: underline;
  margin-bottom: 0;
  cursor: pointer;
  font-family: Syne, sans-serif;
}
.profile-dash {
  color: #222;
  font-size: clamp(0.95rem, 1.2vw, 1.05rem);
  margin-bottom: 0;
  font-family: Syne, sans-serif;
}
.profile-delete-link {
  color: #888;
  font-size: clamp(0.7rem, 1vw, 0.8rem);
  text-decoration: underline;
  margin-bottom: 0.7rem;
  cursor: pointer;
  font-family: Syne, sans-serif;
  display: block;
  margin-top: -0.2rem;
}

.profile-delete-link.no-underline {
  text-decoration: none;
  cursor: default;
}
.profile-streaming-label {
  margin-top: 2.5rem;
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  color: #222;
  font-family: Syne, sans-serif;
  margin-bottom: 1.2rem;
  text-align: center;
}
.profile-streaming-icons {
  display: flex;
  flex-direction: row;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
  justify-content: center;
  width: 100%;
}
.profile-circle {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-streaming-icon-black {
  width: 32px;
  height: 32px;
  filter: brightness(0) invert(1);
}
.profile-right {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  padding: 2.5vw 2vw 2vw 2vw;
  min-width: 0;
  min-height: 0;
  max-height: 80vh;
  overflow: hidden;
  transition: padding 0.2s;
}
.profile-right.scrollable {
  overflow-y: auto;
  max-height: 600px;
}
.profile-title {
  font-size: clamp(1.1rem, 2vw, 1.6rem);
  font-family: Syne, sans-serif;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #222;
  text-align: center;
}
.profile-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 8px;
  padding: 4px;
  align-self: center;
  justify-content: center;
  background-color: #fff;
}
.profile-loading, .profile-empty {
  color: #888;
  font-size: 1.1rem;
  text-align: center;
  margin-top: 2rem;
}
.profile-close {
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: transparent;
  border: 2px solid #222;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  font-size: 1.1rem;
  color: #222;
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(1px);
}
.profile-support-buttons {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.2rem;
  margin-bottom: 1.2rem;
  flex-wrap: wrap;
}
.profile-support-btn {
  font-family: Syne, sans-serif;
  font-size: clamp(0.9rem, 1vw, 1rem);
  font-weight: 600;
  padding: 0.5rem 1rem;
  border: none;
  text-decoration: none;
  color: #fff;
  background: #222;
  transition: background 0.2s, color 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 0.5rem;
  text-align: center;
  display: inline-block;
}
.profile-support-btn.tip {
  background: #424242;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.profile-support-btn.donate {
  background: #0070ba;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.support-icon {
  width: 1.2rem;
  height: 1.2rem;
  object-fit: contain;
}
.profile-support-btn:hover {
  filter: brightness(1.1);
  color: #fff;
}
.profile-bottom-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.7rem;
  flex-wrap: wrap;
}

@media (max-width: 991px) {
  .profile-overlay {
    align-items: flex-start;
    justify-content: center;
  }
  .profile-container {
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 98vw;
    height: auto;
    max-height: 100vh;
    margin-left: auto;
    margin-right: auto;
    box-sizing: border-box;
    border-radius: 0;
    overflow: auto;
    box-sizing: border-box;
    transition: width 0.2s, height 0.2s;
  }
  .profile-left {
    width: 100%;
    min-width: 0;
    max-width: 100vw;
    border-radius: 0;
    min-height: unset;
    padding: 2rem 0 1rem 0;
    box-sizing: border-box;
    flex-basis: auto;
  }
  .profile-left-inner {
    min-height: unset;
    padding: 0;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
  .profile-right {
    padding: 1.5rem 1rem 1.5rem 1rem;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
    transition: padding 0.2s;
  }
  .profile-title {
    font-size: 1.3rem;
    margin-bottom: 1rem;
  }
  .profile-grid {
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 6px;
    padding: 2px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .profile-close {
    position: absolute;
    top: 1.2rem;
    right: 1.2rem;
    transform: translateY(1px);
    width: 2.2rem;
    height: 2.2rem;
    font-size: 1.4rem;
  }
  .profile-support-buttons {
    flex-direction: row;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
  }
  .profile-support-btn {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
  }
  .profile-delete-link {
    font-size: 0.6rem;
    margin-bottom: 0.7rem;
  }
}
@media (max-width: 600px) {
  .profile-overlay {
    align-items: flex-start;
    justify-content: center;
  }
  .profile-container {
    min-width: 0;
    min-height: 0;
    width: 100vw;
    height: auto;
    max-height: 100vh;
    border-radius: 0;
    overflow: auto;
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
  }
  .profile-left {
    padding: 1.2rem 0 0.5rem 0;
  }
  .profile-right {
    padding: 1rem 0.5rem 1rem 0.5rem;
    max-height: none;
    min-height: 0;
    overflow-y: auto;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  .profile-title {
    font-size: 1.1rem;
  }
  .profile-grid {
    /* grid-template-columns will be set dynamically via style */
    grid-gap: 4px;
    padding: 1px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .profile-close {
    position: absolute;
    top: 1.2rem;
    right: 1.2rem;
    transform: translateY(1px);
    width: 2.2rem;
    height: 2.2rem;
    font-size: 1.4rem;
  }
  .profile-support-buttons {
    flex-direction: row;
    gap: 0.7rem;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
  .profile-support-btn {
    font-size: 0.85rem;
    padding: 0.4rem 0.8rem;
    margin-bottom: 0.3rem;
    box-sizing: border-box;
    white-space: nowrap;
  }
  .profile-streaming-label {
    margin-top: 1rem;
    margin-bottom: 0.7rem;
  }
  .profile-support-buttons {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .profile-link {
    margin-bottom: 0.4rem;
  }
}

/* Modal dissolve animations */
.profile-fade-enter-active,
.profile-fade-leave-active {
  transition: opacity 1.5s ease;
}

.profile-fade-enter,
.profile-fade-leave-to {
  opacity: 0;
}

.profile-fade-enter-to,
.profile-fade-leave {
  opacity: 1;
}
</style> 